import { Activity } from '@spike/activity-model';
import clsx from 'clsx';
import { ActivityForm } from 'components/Activities/Form/ActivityForm';
import { ALL_ACTIVITY_TYPES } from 'components/Activities/Icon/ActivityIcon';
import styles from 'components/Activities/Timeline/ActivityTimeline.module.scss';
import { ActivityTimelineEmptyState } from 'components/Activities/Timeline/EmptyState/ActivityTimelineEmptyState';
import { ActivityTimelineInfiniteScroll } from 'components/Activities/Timeline/InfiniteScroll/ActivityTimelineInfiniteScroll';
import Spinner from 'components/UI/V3/Spinner';
import { TimelineGroup } from 'components/UI/V3/Timeline/Group/TimelineGroup';
import { Timeline } from 'components/UI/V3/Timeline/Timeline';
import { Moment } from 'moment-timezone';
import React from 'react';
import { useActivityTimeline } from './ActivityTimelineProvider';
import { ActivityTimelineItem } from './Item/ActivityTimelineItem';
import { ActivityTimelineNoResults } from './NoResults/ActivityTimelineNoResults';

interface ActivityTimelineProps {
    infiniteScrollTrigger?: Element | React.RefObject<HTMLElement> | null;
    readOnly?: boolean;
    children?: React.ReactNode;
    classNames?: {
        root?: string;
        timeline?: string;
    };
}

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
    infiniteScrollTrigger,
    readOnly = false,
    classNames,
    children
}) => {
    const {
        pet,
        pets,
        bookingId,
        customerId,
        isFetching,
        isFirstRender,
        activities,
        page,
        isLoading,
        activityTypes,
        datePeriod,
        showActivityForm,
        activityType,
        activityToEdit,
        createActivityDateLimits,
        createActivityAllowedDates,
        createActivityDateTimeRanges,
        closeActivityForm,
        zIndex
    } = useActivityTimeline();

    const activitiesByDate = React.useMemo(() => {
        return activities.reduce((acc: Array<{ date: Moment; activities: Array<Activity> }>, activity) => {
            const existingGroup = acc.find(group => group.date.isSame(activity.date, 'day'));

            if (existingGroup) {
                existingGroup.activities.push(activity);
            } else {
                acc.push({
                    date: activity.date,
                    activities: [activity]
                });
            }

            return acc;
        }, []);
    }, [activities]);

    const renderState = React.useMemo(() => {
        const areFiltersUsed = activityTypes.length !== ALL_ACTIVITY_TYPES.length || datePeriod !== null;

        if (isFirstRender) {
            return 'loading';
        }

        if (isFetching && page === 1) {
            return 'loading-with-filters';
        }

        if (activities.length === 0 && !isFetching && !areFiltersUsed) {
            return 'empty';
        }

        if (activities.length === 0 && !isFetching && areFiltersUsed) {
            return 'no-results';
        }

        return 'timeline';
    }, [isFirstRender, isFetching, page, activities.length, activityTypes.length, datePeriod]);

    const renderContent = () => {
        switch (renderState) {
            case 'loading':
                return (
                    <div className={styles.loading}>
                        <Spinner />
                    </div>
                );
            case 'empty':
                return <ActivityTimelineEmptyState readOnly={readOnly} />;
            case 'no-results':
                return (
                    <>
                        {children}

                        <ActivityTimelineNoResults />
                    </>
                );
            case 'loading-with-filters':
                return (
                    <>
                        {children}

                        <div className={styles.loading}>
                            <Spinner />
                        </div>
                    </>
                );
            case 'timeline':
                return (
                    <>
                        {children}

                        <Timeline className={classNames?.timeline}>
                            {activitiesByDate.map(item => (
                                <TimelineGroup
                                    key={item.date.toISOString()}
                                    label={item.date}
                                >
                                    {item.activities.map(activity => (
                                        <ActivityTimelineItem
                                            key={activity.id}
                                            activity={activity}
                                            showActions={!readOnly}
                                        />
                                    ))}
                                </TimelineGroup>
                            ))}

                            {isLoading && (
                                <div className={styles.loading}>
                                    <Spinner />
                                </div>
                            )}
                        </Timeline>
                    </>
                );
        }
    };

    return (
        <div className={clsx(styles.root, classNames?.root)}>
            {showActivityForm && (
                <ActivityForm
                    pets={pets}
                    petIds={pet.id ? [pet.id] : []}
                    type={activityType}
                    bookingId={bookingId}
                    customerId={customerId}
                    customerName={pet.clientName}
                    activityToEdit={activityToEdit}
                    dateLimits={createActivityDateLimits}
                    allowedDates={createActivityAllowedDates}
                    availableDateTimeRanges={createActivityDateTimeRanges}
                    zIndex={zIndex}
                    onClose={closeActivityForm}
                />
            )}

            {renderContent()}

            {infiniteScrollTrigger && <ActivityTimelineInfiniteScroll trigger={infiniteScrollTrigger} />}
        </div>
    );
};
